import {ReactElement, useEffect} from "react";
import {useParams} from "react-router";
import {ErrorMessage} from "components/UIComponents/ErrorMessage";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "./menuEditorPageSlice";
import {authSelectors} from "features/auth/authSlice";
import Editor from "../editor/Editor";
import {Spinner} from "components/UIComponents/Spinner";
import {FormattedMessage} from "react-intl";
import {MenuDocument} from "../editor/MenuDocumentModel";
import {menuManagementActions, menuManagementSelectors} from "./menumanagement/menuManagementSlice";
import {useAlgoliaProductIndex} from "../algolia/algoliaHooks";
import {isReady} from "lib/Loadable";

export interface MenuEditorPageProps {

}

export function MenuEditorPage(props: MenuEditorPageProps): ReactElement {
    const dispatch = useAppDispatch();
    const menuId = useParams()['menuId'];
    const userId = useAppSelector(authSelectors.customer);
    const menuName = useAppSelector(menuManagementSelectors.menuName(userId, menuId));
    const document = useAppSelector(menuEditorSelectors.documentContent);
    const menuLoading = useAppSelector(menuEditorSelectors.loading);
    const error = useAppSelector(menuEditorSelectors.error);
    const algoliaProductIndex = useAlgoliaProductIndex(); // init algolia so it's faster on winepicker open...

    const loading = menuLoading || !isReady(algoliaProductIndex);

    const saveDocument = (document: MenuDocument) => {
        if (userId == null || menuId == null || menuName == null) {
            dispatch(menuEditorActions.setError(`incomplete file info, userId: ${userId}, menuId: ${menuId}, menuName: ${menuName}`));
            return;
        }

        return dispatch(menuEditorActions.saveDocument({
            userId: userId,
            fileId: menuId,
            name: menuName,
            document: document
        }));
    };

    useEffect(() => {
        if (userId == null) {
            dispatch(menuEditorActions.setError("editorpage.error.noUser"));
        }
    }, [dispatch, userId]);

    useEffect(() => {
        if (menuId != null && menuName == null && error == null && userId != null) {
            dispatch(menuManagementActions.fetchMenusIfNecessary(userId));
        }
    }, [dispatch, menuId, error, menuName, userId]);

    useEffect(() => {
        if (menuId != null && userId != null && !menuLoading && error == null) {
            dispatch(menuEditorActions.fetchMenuDocumentIfNecessary({userId: userId, fileId: menuId}));
        }
    }, [dispatch, menuId, userId, menuLoading, error]);

    return (
        <>
            {(!document && loading) &&
              <div className="w-screen h-screen flex justify-center items-center">
                <Spinner className="w-10 h-10 m-auto"/>
              </div>
            }
            {error != null &&
              <div className="w-screen h-screen flex justify-center items-center">
                  <ErrorMessage><FormattedMessage id={error}/></ErrorMessage>
              </div>
            }
            {document != null && menuName != null &&
              <Editor
                backUrl=".."
                menuName={menuName}
                document={document}
                saveInProgress={loading}
                onDocumentSave={saveDocument}
              />
            }
        </>
    )
}